//Libs
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
//Componentes material
import CssBaseline from '@material-ui/core/CssBaseline';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormHelperText from '@material-ui/core/FormHelperText';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core';
//Componentes
import VitalizePaper from '../components/VitalizePaper';
import LabelSecao from '../components/LabelSecao';
import Layout from './Layout';
import VitalizeFab from '../components/VitalizeFab';
//Controllers
import ClienteController from '../controllers/ClienteController';
import EmpresaController from '../controllers/EmpresaController';
import PermissaoController from '../controllers/PermissaoController';
import QuestionarioController from '../controllers/QuestionarioController';
import AvaliacaoController from '../controllers/AvaliacaoController';
//Utils
import deepCopy from '../utils/deepCopy';
import { converterNumero } from '../utils/validacao';
import { formatarDataUTC } from '../utils/formatarData';

/** Importando documentação de modelos utilizados
 * @typedef {import('../models/Cliente').default} Cliente
 * @typedef {import('../models/Questionario').default} Questionario
 * @typedef {import('../models/Avaliacao').default} Avaliacao
 */

/**
 * Classe para controle da seleção das avaliações a serem exibidas
 */
class AvaliacaoSelecao {

    /**
     * Cria uma nova instância com controle de avaliação
     * @param {Avaliacao} avaliacao Avaliação que será representada pelo objeto
     */
    constructor(avaliacao) {
        /** @member {Avaliacao} avaliacao Avaliação a ser selecionada */
        this.avaliacao = avaliacao;
        /** @member {String} titulo Título a ser exibido */
        this.titulo = formatarDataUTC(avaliacao.dataCadastro);
        /** @member {Number} id Identificação para controle */
        this.id = avaliacao.id;
        /** @member {Boolean} selecionado Indica se a avaliação está selecionada ou não */
        this.selecionado = false;
    }
}

/**
 * Estilos do componente
 */
const style = () => ({
    avaliacaoOpcao: {
        margin: 0,
        width: '100%'
    }
});

class ResultadoFilterPage extends Component {
    constructor(props) {
        super(props);
        //Bindings
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleEmpresaChange = this.handleEmpresaChange.bind(this);
        this.getEmpresaLabel = this.getEmpresaLabel.bind(this);
        this.getEmpresaSelected = this.getEmpresaSelected.bind(this);
        this.handleQuestionarioChange = this.handleQuestionarioChange.bind(this);
        this.getQuestionarioLabel = this.getQuestionarioLabel.bind(this);
        this.getQuestionarioSelected = this.getQuestionarioSelected.bind(this);
        this.handleClienteChange = this.handleClienteChange.bind(this);
        this.getClienteLabel = this.getClienteLabel.bind(this);
        this.getClienteSelected = this.getClienteSelected.bind(this);
        this.handleQuantidadeChange = this.handleQuantidadeChange.bind(this);
        this.handleValiacaoCheckedChange = this.handleValiacaoCheckedChange.bind(this);
        //Controllers
        this.questionarioController = new QuestionarioController();
        this.clienteController = new ClienteController();
        this.empresaController = new EmpresaController();
        this.avaliacaoController = new AvaliacaoController();
        this.permissaoController = new PermissaoController();
        //Estado inicial
        this.state = {
            erros: {
                empresa: '',
                cliente: '',
                questionario: '',
                quantidadeAvaliacoes: '',
                avaliacoes: ''
            },
            valores: {
                empresa: null,
                cliente: null,
                questionario: null,
                quantidadeAvaliacoes: '5'
            },
            questionarios: [],
            empresas: [],
            clientes: [],
            /** @type {Array<AvaliacaoSelecao>} Lista de avaliações para seleção */
            avaliacoes: [],
            permiteSelecionarEmpresa: false,
            permiteSelecionarCliente: false,
            redirecionar: false,
            carregando: true
        };
    }

    async componentDidMount() {
        //Cria uma cópia do estado e verifica se o usuário pode selecionar a empresa
        const state = deepCopy(this.state);
        state.carregando = false;
        state.permiteSelecionarEmpresa = await this.permissaoController.permiteSelecionarEmpresa();
        state.permiteSelecionarCliente = await this.permissaoController.permiteSelecionarCliente();

        //Caso permita selecionar a empresa, carrega a listagem
        if (state.permiteSelecionarEmpresa) {
            state.empresas = await this.obterEmpresas();
        } else { //Caso contrário carrega os clientes e os questionários
            if (state.permiteSelecionarCliente)
                state.clientes = await this.obterClientes();
            state.questionarios = await this.obterQuestionarios();
        }

        //Atualiza o estado
        this.setState(state);
    }

    /**
     * Trata a alteração do campo empresa, recarregando os dados de clientes e questionários
     * @param {*} evt Objeto contendo os dados do evento
     * @param {Object} novaEmpresa Dados da empresa selecionada
     * @param {Number} novaEmpresa.id Identificação da empresa
     */
    async handleEmpresaChange(evt, novaEmpresa) {
        this.setState({ carregando: true });
        //Cria uma cópia do estado atual e atualiza o campo
        const state = deepCopy(this.state);
        state.carregando = false;
        state.valores.empresa = novaEmpresa;

        //Se tiver uma empresa selecionada, pesquisa na API
        if (state.valores.empresa) {
            if (state.permiteSelecionarCliente)
                state.clientes = await this.obterClientes(state.valores.empresa.id);
            state.questionarios = await this.obterQuestionarios(state.valores.empresa.id);
        } else { //Se nenhuma tiver sido selecionada, deixa a lista vazia
            state.clientes = [];
            state.questionarios = [];
        }

        //Atualiza o estado da página
        this.setState(state);
    }

    /**
     * Retorna os dados da empresa para exibição no campo
     * @param {Object} empresa Dados da empresa
     * @param {String} empresa.nome Nome da empresa
     * @returns {String} Retorna o nome da empresa
     */
    getEmpresaLabel(empresa) {
        return empresa.nome;
    }

    /**
     * Verifica se o item do campo está selecionado
     * @param {Object} opcao Opção exibida no campo
     * @param {Number} opcao.id Identificação da empresa
     * @param {Object} valor Opção selecionada
     * @param {Number} valor.id Identificação da empresa selecionada
     * @returns {Boolean} Retorna verdadeiro caso a opção esteja selecionada
     */
    getEmpresaSelected(opcao, valor) {
        //Compara a identificação das duas empresas
        return opcao.id === valor.id;
    }

    /**
     * Trata a alteração do campo questionário, recarregando os dados da avaliação
     * @param {*} evt Objeto contendo os dados do evento
     * @param {Object} novoQuestionario Dados do quesitonário selecionado
     * @param {Number} novoQuestionario.id Identificação do questionário
     */
    async handleQuestionarioChange(evt, novoQuestionario) {
        //Cria uma cópia do estado atual e atualiza o campo
        const state = deepCopy(this.state);
        //Atualiza o valor do questionário
        state.valores.questionario = novoQuestionario;
        //Se o cliente está preenchido
        if (state.valores.cliente) {
            state.avaliacoes = await this.obterAvaliacoes(state.valores.cliente.id, state.valores.questionario.id);
            // Se o usuário for o cliente (não permite alterar o cliente)
        } else if (!state.permiteSelecionarCliente) {
            state.avaliacoes = await this.obterAvaliacoes(null, state.valores.questionario.id);
        }
        //Atualiza o status
        this.setState(state);
    }

    /**
     * Retorna os dados do questionário para exibição no campo
     * @param {Object} questionario Dados do questionário
     * @param {String} questionario.titulo Título do questionário
     * @returns {String} Retorna o título do questionário
     */
    getQuestionarioLabel(questionario) {
        return questionario.titulo;
    }

    /**
     * Verifica se o item do campo está selecionado
     * @param {Object} opcao Opção exibida no campo
     * @param {Number} opcao.id Identificação da empresa
     * @param {Object} valor Opção selecionada
     * @param {Number} valor.id Identificação da empresa selecionada
     * @returns {Boolean} Retorna verdadeiro caso a opção esteja selecionada
     */
    getQuestionarioSelected(opcao, valor) {
        //Compara a identificação dos dois questionários
        return opcao.id === valor.id;
    }

    /**
     * Trata a alteração do campo cliente, recarregando os dados da avaliação
     * @param {*} evt Objeto contendo os dados do evento
     * @param {Object} novoCliente Dados do cliente selecionado
     * @param {Number} novoCliente.id Identificação do cliente
     */
    async handleClienteChange(evt, novoCliente) {
        //Cria uma cópia do estado atual e atualiza o campo
        const state = deepCopy(this.state);
        //Atualiza o valor do cliente
        state.valores.cliente = novoCliente;
        //Se o questionário está preenchido
        if (state.valores.questionario) {
            state.avaliacoes = await this.obterAvaliacoes(state.valores.cliente.id, state.valores.questionario.id);
        }
        //Atualiza o status
        this.setState(state);
    }

    /**
     * Retorna os dados do cliente para exibição no campo
     * @param {Object} cliente Dados do cliente
     * @param {String} cliente.titulo Título do cliente
     * @returns {String} Retorna o título do cliente
     */
    getClienteLabel(cliente) {
        return cliente.nome;
    }

    /**
     * Verifica se o item do campo está selecionado
     * @param {Object} opcao Opção exibida no campo
     * @param {Number} opcao.id Identificação da empresa
     * @param {Object} valor Opção selecionada
     * @param {Number} valor.id Identificação da empresa selecionada
     * @returns {Boolean} Retorna verdadeiro caso a opção esteja selecionada
     */
    getClienteSelected(opcao, valor) {
        //Compara a identificação dos dois clientes
        return opcao.id === valor.id;
    }

    /**
     * Trata a alteração no campo de quantidade de avaliações
     * @param {*} event Objeto contendo os dados do evento
     */
    handleQuantidadeChange(event) {
        //Cria uma cópia do estado atual e atualiza o campo
        const state = deepCopy(this.state);
        state.valores.quantidadeAvaliacoes = event.target.value;
        //Atualiza o status
        this.setState(state);
    }

    /**
     * Trata o vento de alteração do checkbox das avaliações selecionadas
     * @param {*} event Dados do evento
     */
    handleValiacaoCheckedChange(event) {
        //Cria uma cópia do estado atual e atualiza o campo
        const state = deepCopy(this.state);
        //Obtém o id do item selecionado
        const id = converterNumero(event.target.name);
        //Identifica a avaliação selecionada
        const avaliacao = state.avaliacoes.find(a => a.id === id);
        //Atualiza a marcação da avaliação
        avaliacao.selecionado = event.target.checked;
        //Atualiza o status
        this.setState(state);
    }

    /**
     * Trata a submissão do formulário com os dados do filtro
     * @param {*} event Objeto contendo os dados do evento
     */
    handleSubmit(event) {
        //Previne que outras ações sejam realizadas
        event.preventDefault();
        //Valida as avaliações selecionadas
        const { valores, permiteSelecionarEmpresa, permiteSelecionarCliente, avaliacoes } = this.state;
        const { empresa, cliente, questionario, quantidadeAvaliacoes } = valores;
        const erros = { empresa: '', cliente: '', questionario: '', quantidadeAvaliacoes: '', avaliacoes: '' };
        const quantidadeInteiro = converterNumero(quantidadeAvaliacoes);
        const selecionadas = avaliacoes.filter(avaliacao => avaliacao.selecionado);
        let redirecionar = true;

        //Valida o campo empresa
        if (permiteSelecionarEmpresa && (!empresa || !empresa.id)) {
            erros.empresa = 'Selecione uma empresa';
            redirecionar = false;
        }

        //Valida o campo cliente
        if (permiteSelecionarCliente && (!cliente || !cliente.id)) {
            erros.cliente = 'Selecione um formando';
            redirecionar = false;
        }

        //Valida o campo questionário
        if (!questionario || !questionario.id) {
            erros.questionario = 'Selecione um questionário';
            redirecionar = false;
        }

        //Valida o campo quantidade de avaliações
        if (isNaN(quantidadeInteiro) || quantidadeInteiro < 1 || quantidadeInteiro > 5) {
            erros.quantidadeAvaliacoes = 'Selecione quantas avaliações serão comparadas';
            redirecionar = false;
        }

        //Valida as avaliações selecionadas
        if (selecionadas.length !== quantidadeInteiro) {
            erros.avaliacoes = `Selecione ${quantidadeInteiro} ${quantidadeInteiro === 1 ? 'avaliação' : 'avaliações'}`;
            redirecionar = false;
        }

        //Atualiza o estado da página
        this.setState({ redirecionar, erros });
    }

    /**
     * Carrega as empresas da API
     * @returns {Array} Retorna a lista de empresas, ou uma lista vazia em caso de erros
     */
    async obterEmpresas() {
        try {
            const result = await this.empresaController.listarSelecao();
            if (result.sucesso)
                return result.items;
            else
                return [];
        } catch (ex) {
            //Em caso de erro retorna uma lista vazia
            return [];
        }
    }

    /**
     * Carrega os clientes da API
     * @param {Number} empresa Identificação da empresa selecionada
     * @returns {Array} Retorna a lista de clientes, ou uma lista vazia em caso de erros
     */
    async obterClientes(empresa) {
        try {
            const result = await this.clienteController.listarSelecao(empresa);
            if (result.sucesso)
                return result.items;
            else
                return [];
        } catch (ex) {
            //Em caso de erro retorna uma lista vazia
            return [];
        }
    }

    /**
     * Carrega os questionário da API
     * @param {Number} empresa Identificação da empresa selecionada
     * @returns {Array} Retorna a lista de questionários, ou uma lista vazia em caso de erros
     */
    async obterQuestionarios(empresa) {
        try {
            const result = await this.questionarioController.listarSelecao(empresa);
            if (result.sucesso)
                return result.items;
            else
                return [];
        } catch (ex) {
            //Em caso de erro retorna uma lista vazia
            return [];
        }
    }

    /**
     * Consulta as avaliações disponíveis para o cliente e o questionário informados
     * @param {Number} cliente Identificação do cliente a ser pesquisado
     * @param {Number} questionario Identificação do questionário a ser pesquisado
     * @return {Array<AvaliacaoSelecao>} Retorna uma lista e itens para avaliação
     */
    async obterAvaliacoes(cliente, questionario) {
        try {
            const result = await this.avaliacaoController.listarSelecao(cliente, questionario);
            if (result.sucesso) {
                //Reverte a ordem das avaliações, mantendo as mais novas primeiro
                const items = result.items.reverse().map(avaliacao => new AvaliacaoSelecao(avaliacao));
                //Identifica quantas devem ser selecionadas
                const quantidade = this.state.valores.quantidadeAvaliacoes * 1;
                //Marca as 'n' primeiras como selecionadas
                items.slice(0, quantidade).forEach(selecao => selecao.selecionado = true);
                return items;
            }
            else
                return [];
        } catch (ex) {
            return [];
        }
    }

    /**
     * Monta a url para impressão do resultado da avaliação
     * @returns {String} Retorna a URL de destino do redirecionamento
     */
    formatLink() {
        const selecionadas = this.state.avaliacoes.filter(avaliacao => avaliacao.selecionado);
        const parametros = selecionadas.map(avaliacao => `avaliacoes[]=${avaliacao.id}`).join('&');
        return `/avaliacao/resultado?${parametros}`;
    }

    /**
     * Renderiza o campo de seleção de empresa, caso o usuário possua permissão 
     * @param {Boolean} permiteSelecionarEmpresa Indica se o usuário pode selecionar uma empresa
     * @param {String} empresa Valor selecionado
     * @param {String} empresaErro Mensagem de erro do campo
     * @param {Array} empresas Lista de empresas retornadas da API
     * @returns {React.Component} Retorna o componente ou null
     */
    renderCampoEmpresas(permiteSelecionarEmpresa, empresa, empresaErro, empresas) {
        if (permiteSelecionarEmpresa)
            return (
                <Grid item xs={12} >
                    <Autocomplete
                        id="select-empresa"
                        options={empresas}
                        value={empresa}
                        getOptionLabel={this.getEmpresaLabel}
                        getOptionSelected={this.getEmpresaSelected}
                        onChange={this.handleEmpresaChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                id="empresa"
                                name="empresa"
                                label="Empresa"
                                native
                                required
                                variant="outlined"
                                error={!!empresaErro}
                                helperText={empresaErro}
                                fullWidth
                            />
                        )}
                    />
                </Grid>
            );
        else
            return null;
    }

    /**
     * Renderiza o campo de seleção de cliente, caso o usuário possua permissão 
     * @param {Boolean} permiteSelecionarCliente Indica se o usuário pode selecionar um cliente
     * @param {String} cliente Valor selecionado
     * @param {String} clienteErro Mensagem de erro do campo
     * @param {Array} clientes Lista de clientes retornados da API
     * @returns {React.Component} Retorna o componente ou null
     */
    renderCampoClientes(permiteSelecionarCliente, cliente, clienteErro, clientes) {
        if (permiteSelecionarCliente)
            return (
                <Grid item xs={12} sm={6} >
                    <Autocomplete
                        id="select-cliente"
                        options={clientes}
                        value={cliente}
                        getOptionLabel={this.getClienteLabel}
                        getOptionSelected={this.getClienteSelected}
                        onChange={this.handleClienteChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                id="cliente"
                                name="cliente"
                                label="Formando"
                                native
                                required
                                variant="outlined"
                                error={!!clienteErro}
                                helperText={clienteErro}
                                fullWidth
                            />
                        )}
                    />
                </Grid>
            );
        else
            return null;
    }

    render() {
        const { onLogout, classes, usuario, empresa: empresaLogin, permissoes } = this.props;
        const { empresas, clientes, questionarios, avaliacoes, permiteSelecionarEmpresa, permiteSelecionarCliente, carregando } = this.state;
        const { empresa: empresaErro, cliente: clienteErro, questionario: questionarioErro, quantidadeAvaliacoes: quantidadeAvaliacoesErro, avaliacoes: avaliacoesErro } = this.state.erros;
        const { empresa, cliente, questionario, quantidadeAvaliacoes } = this.state.valores;

        if (this.state.redirecionar)
            return <Redirect to={this.formatLink()} />;

        return (
            <Layout onLogout={onLogout} usuario={usuario} empresa={empresaLogin} permissoes={permissoes} carregando={carregando} >
                <CssBaseline />
                <form onSubmit={this.handleSubmit} noValidate >
                    <VitalizePaper>
                        <Grid container spacing={1}>
                            <Grid item xs={12} >
                                <LabelSecao label="Imprimir resultados" />
                            </Grid>
                            {
                                this.renderCampoEmpresas(permiteSelecionarEmpresa, empresa, empresaErro, empresas)
                            }
                            {
                                this.renderCampoClientes(permiteSelecionarCliente, cliente, clienteErro, clientes)
                            }
                            <Grid item xs={12} sm={6} >
                                <Autocomplete
                                    id="select-questionario"
                                    options={questionarios}
                                    value={questionario}
                                    getOptionLabel={this.getQuestionarioLabel}
                                    getOptionSelected={this.getQuestionarioSelected}
                                    onChange={this.handleQuestionarioChange}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            id="questionario"
                                            name="questionario"
                                            label="Questionário"
                                            native
                                            required
                                            variant="outlined"
                                            error={!!questionarioErro}
                                            helperText={questionarioErro}
                                            fullWidth
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </VitalizePaper>
                    <VitalizePaper>
                        <Grid container spacing={1}>
                            <Grid item xs={12} >
                                <FormControl component="fieldset" id="quantidade-avaliacoes" fullWidth error={!!quantidadeAvaliacoesErro} >
                                    <FormLabel component="legend">Avaliações a comparar</FormLabel>
                                    <RadioGroup row value={quantidadeAvaliacoes} name="quantidadeAvaliacoes" onChange={this.handleQuantidadeChange} >
                                        <Grid container spacing={1} >
                                            <Grid item xs><FormControlLabel className={classes.avaliacaoOpcao} value="1" control={<Radio />} label="1" labelPlacement="bottom" /></Grid>
                                            <Grid item xs><FormControlLabel className={classes.avaliacaoOpcao} value="2" control={<Radio />} label="2" labelPlacement="bottom" /></Grid>
                                            <Grid item xs><FormControlLabel className={classes.avaliacaoOpcao} value="3" control={<Radio />} label="3" labelPlacement="bottom" /></Grid>
                                            <Grid item xs><FormControlLabel className={classes.avaliacaoOpcao} value="4" control={<Radio />} label="4" labelPlacement="bottom" /></Grid>
                                            <Grid item xs><FormControlLabel className={classes.avaliacaoOpcao} value="5" control={<Radio />} label="5" labelPlacement="bottom" /></Grid>
                                        </Grid>
                                    </RadioGroup>
                                    <FormHelperText>{quantidadeAvaliacoesErro}</FormHelperText>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </VitalizePaper>
                    <VitalizePaper>
                        <Grid container spacing={1} >
                            <Grid item xs={12} >
                                <LabelSecao label="Selecione as avaliações" />
                            </Grid>
                            {
                                avaliacoes.length === 0 && (
                                    <Grid item xs={12} >
                                        <Typography>Nenhuma avalição encontrada</Typography>
                                    </Grid>
                                )
                            }
                            {
                                avaliacoes.map(avaliacao => (
                                    <Grid item key={avaliacao.id} >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={avaliacao.selecionado}
                                                    onChange={this.handleValiacaoCheckedChange}
                                                    name={avaliacao.id}
                                                />
                                            }
                                            label={avaliacao.titulo}
                                        />
                                    </Grid>
                                ))
                            }
                            {
                                avaliacoesErro && (
                                    <Grid item xs={12} >
                                        <Typography>{avaliacoesErro}</Typography>
                                    </Grid>
                                )
                            }
                        </Grid>
                    </VitalizePaper>
                    <VitalizeFab color="primary" variant="extended" type="submit" >
                        Gerar relatório
                    </VitalizeFab>
                </form>
            </Layout>
        );
    }
}

ResultadoFilterPage.propTypes = {
    /** Callback quando o usuário realiza o logout da aplicação */
    onLogout: PropTypes.func,
    /** Dados do usuário autenticado */
    usuario: PropTypes.object.isRequired,
    /** Empresa relacionada ao usuário */
    empresa: PropTypes.object,
    /** Objeto contendo as permissões do usuário */
    permissoes: PropTypes.object.isRequired,
    /** Classes CSS da página */
    classes: PropTypes.shape({
        avaliacaoOpcao: PropTypes.string
    })
};

export default withStyles(style)(ResultadoFilterPage);